import React, { useState, useEffect } from 'react';
import { driversAPI, usersAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    license_number: '',
    license_expiry: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    date_of_birth: '',
    hire_date: '',
    status: 'active',
    user_id: '',
  });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadDrivers();
    usersAPI.getAll().then(setUsers).catch(() => setUsers([]));
  }, []);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const data = await driversAPI.getAll();
      setDrivers(data);
    } catch (error) {
      alert('Error loading drivers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDriver) {
        await driversAPI.update(editingDriver.id, formData);
      } else {
        await driversAPI.create(formData);
      }
      setShowModal(false);
      setEditingDriver(null);
      resetForm();
      loadDrivers();
    } catch (error) {
      alert('Error saving driver: ' + error.message);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      first_name: driver.first_name || '',
      last_name: driver.last_name || '',
      phone: driver.phone || '',
      email: driver.email || '',
      address: driver.address || '',
      date_of_birth: driver.date_of_birth || '',
      hire_date: driver.hire_date || '',
      status: driver.status || 'active',
      user_id: driver.user_id || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      await driversAPI.delete(id);
      loadDrivers();
    } catch (error) {
      alert('Error deleting driver: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      license_number: '',
      license_expiry: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      address: '',
      date_of_birth: '',
      hire_date: '',
      status: 'active',
      user_id: '',
    });
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'license_number', label: 'License #' },
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'user_id', label: 'User ID', format: (v) => v || '—' },
    { key: 'status', label: 'Status' },
    { key: 'license_expiry', label: 'License Expiry' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Driver Management</h1>
        <button className="btn-primary" onClick={() => { setEditingDriver(null); resetForm(); setShowModal(true); }}>
          Add New Driver
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={drivers}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingDriver(null); resetForm(); }}
        title={editingDriver ? 'Edit Driver' : 'Add New Driver'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>License Number *</label>
            <input
              type="text"
              value={formData.license_number}
              onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>License Expiry</label>
              <input
                type="date"
                value={formData.license_expiry}
                onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="2"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Hire Date</label>
              <input
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>App login user</label>
            <select
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
            >
              <option value="">None</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username} ({u.role})
                </option>
              ))}
            </select>
            <small>Link a user with role Driver so My Route sees this assignment.</small>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingDriver(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DriverManagement;


